
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

interface LeaderboardUser {
  id: string;
  rank: number;
  userName: string;
  avatar?: string;
  level: number;
  points: number;
  promptsCount: number;
  likesReceived: number;
  badges: string[];
  joinDate: string;
}

interface UserStats {
  level: number;
  points: number;
  promptsCount: number;
  likesReceived: number;
  badges: string[];
  joinDate: string;
  rank: number;
}

// Database return type matches the Supabase function's output
interface DbLeaderboardUser {
  id: string;
  rank: number;
  user_name: string;
  avatar: string;
  level: number;
  points: number;
  prompts_count: number;
  likes_received: number;
  badges: string[];
  join_date: string;
}

export const useLeaderboardData = (timeFrame: 'all-time' | 'monthly' | 'weekly') => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState<LeaderboardUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [nextLevelPoints, setNextLevelPoints] = useState(100);
  
  // Calculate how many points needed for next level - logarithmic scale
  const getPointsForLevel = (level: number) => {
    // Using the same calculation as in the Supabase function calculate_level
    // but in reverse to get points needed for next level
    return Math.floor(50 * Math.pow(2, level - 1));
  };

  // Calculate level based on points - should match Supabase function
  const calculateLevel = (points: number) => {
    // This matches the calculate_level function in Supabase
    return Math.floor(1 + Math.log2(Math.max(points, 1) / 50));
  };
  
  useEffect(() => {
    const fetchLeaderboardData = async () => {
      setIsLoading(true);
      
      try {
        // Use the get_leaderboard function if available
        let { data: dbLeaderboardData, error } = await supabase
          .rpc('get_leaderboard', { time_frame: timeFrame });
        
        if (error) {
          console.error("Error using get_leaderboard function:", error);
          
          // Fallback to direct table query if RPC function fails
          const { data: profilesData, error: profilesError } = await supabase
            .from('user_achievements')
            .select('user_id, level, points, prompts_count, likes_received, badges, join_date')
            .order('points', { ascending: false })
            .limit(10);

          if (profilesError) {
            throw profilesError;
          }

          if (!profilesData) {
            throw new Error("No leaderboard data returned");
          }

          // We also need to get username and avatar from profiles table
          const userIds = profilesData.map(item => item.user_id);
          const { data: usersData, error: usersError } = await supabase
            .from('profiles')
            .select('id, username, first_name, last_name, avatar_url')
            .in('id', userIds);

          if (usersError) {
            throw usersError;
          }

          // Map the data to match our LeaderboardUser interface
          const leaderboardData = profilesData.map((item, index) => {
            const userData = usersData?.find(u => u.id === item.user_id) || { 
              username: 'Anonymous', 
              first_name: null, 
              last_name: null,
              avatar_url: null 
            };
            
            const displayName = [userData.first_name, userData.last_name]
              .filter(Boolean)
              .join(' ') || userData.username || 'Anonymous User';
            
            // Recalculate level to ensure consistency
            const level = calculateLevel(item.points);
            
            return {
              id: item.user_id,
              rank: index + 1,
              userName: displayName,
              avatar: userData.avatar_url || undefined,
              level: level, // Use the calculated level
              points: item.points || 0,
              promptsCount: item.prompts_count || 0,
              likesReceived: item.likes_received || 0,
              badges: item.badges || [],
              joinDate: item.join_date
            } as LeaderboardUser;
          });
          
          setLeaderboard(leaderboardData);
        } else if (dbLeaderboardData && Array.isArray(dbLeaderboardData)) {
          // Transform the database result to match our interface
          const formattedLeaderboard: LeaderboardUser[] = (dbLeaderboardData as DbLeaderboardUser[]).map(item => ({
            id: item.id,
            rank: item.rank,
            userName: item.user_name,
            avatar: item.avatar || undefined,
            level: item.level,
            points: item.points,
            promptsCount: item.prompts_count,
            likesReceived: item.likes_received,
            badges: item.badges || [],
            joinDate: item.join_date
          }));
          
          setLeaderboard(formattedLeaderboard);
          
          if (user) {
            // Find the user in the leaderboard
            const userInLeaderboard = formattedLeaderboard.find(item => item.id === user.id);
            
            if (userInLeaderboard) {
              // Format user stats from the leaderboard data
              const userStatsFormatted: UserStats = {
                level: userInLeaderboard.level,
                points: userInLeaderboard.points,
                promptsCount: userInLeaderboard.promptsCount,
                likesReceived: userInLeaderboard.likesReceived,
                badges: userInLeaderboard.badges,
                joinDate: userInLeaderboard.joinDate,
                rank: userInLeaderboard.rank
              };
              
              setUserStats(userStatsFormatted);
              
              // Calculate points needed for next level
              const pointsNeeded = getPointsForLevel(userStatsFormatted.level + 1);
              setNextLevelPoints(pointsNeeded);
            }
          }
        }
        
      } catch (error) {
        console.error("Error fetching leaderboard data:", error);
        toast.error("Could not load leaderboard data. Please try again later.");
        
        // Provide some sample data to ensure the UI works
        const sampleData: LeaderboardUser[] = [
          {
            id: '1',
            rank: 1,
            userName: 'PromptMaster',
            avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=PM',
            level: 42,
            points: 8750,
            promptsCount: 120,
            likesReceived: 540,
            badges: ['Top Contributor', 'Prompt Genius'],
            joinDate: '2023-01-15'
          },
          {
            id: '2',
            rank: 2,
            userName: 'AIWhisperer',
            avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=AW',
            level: 38,
            points: 7200,
            promptsCount: 95,
            likesReceived: 480,
            badges: ['Rising Star', 'Creative Genius'],
            joinDate: '2023-02-20'
          },
          {
            id: '3',
            rank: 3,
            userName: 'PromptNinja',
            avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=PN',
            level: 35,
            points: 6800,
            promptsCount: 88,
            likesReceived: 420,
            badges: ['Consistency King', 'Helpful Hero'],
            joinDate: '2023-03-10'
          }
        ];
        
        setLeaderboard(sampleData);
        
        if (user) {
          setUserStats(null);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchLeaderboardData();
  }, [user, timeFrame]);
  
  return {
    leaderboard,
    isLoading,
    userStats,
    nextLevelPoints
  };
};
